// SettingsPlanCatalogGrid.jsx

import React from 'react';

// Components
import Button from 'components/button/Button';

// Utils
import {
    formatApiSummary,
    formatBytes,
    formatLiveSummary,
    formatPlanPrice,
    formatRecordingSummary,
    formatUserLimit,
    isSelfServeSelectable,
} from '../utils/settingsPlanPaneUtils';

/**
 * SettingsPlanCatalogGrid
 *
 * Renders the public plan catalog grid and the plan action buttons.
 *
 * @param {boolean} canEditWorkspace - Whether the current user can edit workspace settings.
 * @param {string} checkoutPlanKey - Plan key with inline checkout currently open.
 * @param {string} currentPlanKey - Active workspace plan key.
 * @param {boolean} hasActivePaidSubscription - Whether the workspace has active paid billing.
 * @param {function} onSelectPlan - Called when a free plan is selected.
 * @param {function} onStartPaidCheckout - Called when a paid plan checkout is started.
 * @param {array} plans - Public plan catalog rows.
 * @param {string} savingPlanKey - Plan key currently being updated.
 * @param {object} theme - Active theme tokens.
 *
 * @returns {ReactNode} The rendered plan catalog grid.
 */
const SettingsPlanCatalogGrid = React.memo(({
    canEditWorkspace,
    checkoutPlanKey,
    currentPlanKey,
    hasActivePaidSubscription,
    onSelectPlan,
    onStartPaidCheckout,
    plans,
    savingPlanKey,
    theme,
}) => {
    return (
        <div className="settings-modal-plan-grid">
            {plans.map((plan) => {
                const planKey = String(plan?.key || '').trim();
                const isCurrent = planKey === currentPlanKey;
                const isSelectable = isSelfServeSelectable(plan);
                const isSaving = savingPlanKey === planKey;
                const isPaidCheckoutPlan = checkoutPlanKey === planKey;
                const canSelectFreePlan = canEditWorkspace
                    && !isCurrent
                    && !savingPlanKey
                    && !hasActivePaidSubscription;
                const canStartPaidCheckout = canEditWorkspace
                    && !savingPlanKey
                    && !isCurrent
                    && !hasActivePaidSubscription
                    && !isPaidCheckoutPlan
                    && !isSelectable;

                return (
                    <div
                        key={planKey}
                        className="settings-modal-plan-card"
                        style={{
                            borderColor: theme.backgroundHover,
                        }}
                    >
                        <div className="settings-modal-plan-card-header">
                            <div className="settings-modal-plan-card-meta">
                                <div
                                    className="settings-modal-plan-card-title"
                                    style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {plan.title}
                                </div>
                                <div
                                    className="settings-modal-plan-card-price"
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    {formatPlanPrice(plan)}
                                </div>
                            </div>

                            <div
                                className="settings-modal-plan-badge"
                                style={{
                                    backgroundColor: isCurrent ? theme.backgroundHover : 'transparent',
                                    borderColor: theme.backgroundHover,
                                    color: theme.foregroundMuted,
                                }}
                            >
                                {isCurrent ? 'Current' : (isSelectable ? 'Free' : 'Paid')}
                            </div>
                        </div>

                        <div className="settings-modal-plan-facts">
                            <div className="settings-modal-plan-fact">
                                <span
                                    className="settings-modal-plan-fact-label"
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    Users
                                </span>
                                <span
                                    className="settings-modal-plan-fact-value"
                                    style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {formatUserLimit(plan)}
                                </span>
                            </div>

                            <div className="settings-modal-plan-fact">
                                <span
                                    className="settings-modal-plan-fact-label"
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    Storage
                                </span>
                                <span
                                    className="settings-modal-plan-fact-value"
                                    style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {formatBytes(plan.pooledStorageBytes)}
                                </span>
                            </div>

                            <div className="settings-modal-plan-fact">
                                <span
                                    className="settings-modal-plan-fact-label"
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    API
                                </span>
                                <span
                                    className="settings-modal-plan-fact-value"
                                    style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {formatApiSummary(plan)}
                                </span>
                            </div>

                            <div className="settings-modal-plan-fact">
                                <span
                                    className="settings-modal-plan-fact-label"
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    Live rooms
                                </span>
                                <span
                                    className="settings-modal-plan-fact-value"
                                    style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {formatLiveSummary(plan)}
                                </span>
                            </div>

                            <div className="settings-modal-plan-fact">
                                <span
                                    className="settings-modal-plan-fact-label"
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    Recording
                                </span>
                                <span
                                    className="settings-modal-plan-fact-value"
                                    style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {formatRecordingSummary(plan)}
                                </span>
                            </div>
                        </div>

                        <div className="settings-modal-plan-footer">
                            <Button
                                label={isCurrent
                                    ? 'Current Plan'
                                    : isSaving
                                        ? 'Updating...'
                                        : isSelectable
                                            ? hasActivePaidSubscription
                                                ? 'Cancel Billing First'
                                                : 'Select Plan'
                                            : isPaidCheckoutPlan
                                                ? 'Checkout Open'
                                                : hasActivePaidSubscription
                                                    ? 'Billing Change Soon'
                                                    : 'Subscribe'}
                                enabled={isSelectable
                                    ? canSelectFreePlan
                                    : canStartPaidCheckout}
                                onClick={() => {
                                    if (isSelectable) {
                                        onSelectPlan(plan);
                                        return;
                                    }

                                    onStartPaidCheckout(plan);
                                }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

SettingsPlanCatalogGrid.displayName = 'SettingsPlanCatalogGrid';

export default SettingsPlanCatalogGrid;